import { formatInteger } from '@monetize/utils/core';
import { useEffect, useRef, useState } from 'react';
import { MBox, MGrid } from '../../../../components/Monetize';
import { QuoteBodyAccordion } from '../../../../components/Quotes/QuoteBodyAccordion';
import { hasVariableAmountProduct } from '../../../../utils/quotes';
import { QuoteFormTotalRow } from '../../Quote/components/QuoteFormTotalRow';
import { QuoteFormTotalRowAmendV2 } from '../../Quote/components/QuoteFormTotalRowAmendV2';
import { useQuoteOfferingRatesPrefetch } from '../../Quote/components/quoteOffering/useQuoteOfferingRatesPrefetch';
import { useQuoteContext } from '../../Quote/quoteContext';
import { QuoteInternalOfferingsSkeleton } from './QuoteInternalOfferingsSkeleton';
import { QuoteInternalOfferingTable } from './QuoteInternalOfferingTable';

/**
 * Upper bound on the skeleton. The API client sets no request timeout, so a
 * stalled rate request would otherwise hold the whole section; past this the
 * table renders and each row fetches (and reports) its own rate as before.
 */
const OFFERINGS_SKELETON_MAX_WAIT_MS = 8000;

export const QuoteInternalOfferings = ({ value }: { value?: string }) => {
  const { quoteData, useAmendmentV2, productOfferingsData, isReadOnly } =
    useQuoteContext();
  const { quote } = quoteData;

  const anyVariableAmountProducts = hasVariableAmountProduct(quote);
  const offeringCount = quote?.quoteOfferings.filter(
    (quoteOffering) => quoteOffering.parentQuoteOfferingId === null,
  ).length;

  const [hasWaitedLongEnough, setHasWaitedLongEnough] = useState(false);
  useEffect(() => {
    const fallback = setTimeout(
      () => setHasWaitedLongEnough(true),
      OFFERINGS_SKELETON_MAX_WAIT_MS,
    );
    return () => clearTimeout(fallback);
  }, []);

  /**
   * Latches the first time the table may render, and never goes back: only the
   * initial load is gated, so a rate added later loads in place rather than
   * swapping the table out and losing its open/collapsed state.
   */
  const hasLeftSkeleton = useRef(false);

  /**
   * The rows need the offering catalog and each rate's prices before they can
   * render completely. Hold the table behind a skeleton until both are in so
   * every row appears at once, fully formed, instead of filling in piecemeal.
   * The rows carry their own observers once mounted, so this hands off to them.
   */
  const { isPending: isRatesPending } = useQuoteOfferingRatesPrefetch(
    quote?.quoteOfferings,
    // Hands off in the same render that latches below, rather than one later:
    // reading the ref alone would leave a stalled prefetch mounted while the
    // rows take over on the fallback path.
    { enabled: !hasLeftSkeleton.current && !hasWaitedLongEnough },
  );

  if (
    (!productOfferingsData.loading && !isRatesPending) ||
    hasWaitedLongEnough
  ) {
    hasLeftSkeleton.current = true;
  }
  const showSkeleton = !hasLeftSkeleton.current;

  return (
    <QuoteBodyAccordion
      value={value}
      title={`Products and Pricing (${formatInteger(offeringCount ?? 0)} included)`}
    >
      {showSkeleton ? (
        <QuoteInternalOfferingsSkeleton
          quoteOfferings={quote?.quoteOfferings ?? []}
          canEditDescriptions={!isReadOnly}
          useAmendmentV2={useAmendmentV2}
        />
      ) : (
        <QuoteInternalOfferingTable />
      )}

      <MGrid
        gridTemplateColumns={useAmendmentV2 ? '1fr 2fr' : '8fr 3.2fr'}
        gridTemplateRows="1fr"
        columnGap={4}
        w="full"
      >
        <MBox />

        {useAmendmentV2 ? (
          <QuoteFormTotalRowAmendV2
            anyVariableAmountProducts={anyVariableAmountProducts}
          />
        ) : (
          <QuoteFormTotalRow
            anyVariableAmountProducts={anyVariableAmountProducts}
          />
        )}
      </MGrid>
    </QuoteBodyAccordion>
  );
};
